import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    ArrowLeft,
    ArrowRight,
    Calendar,
    CheckCircle2,
    Clock3,
    Copy,
    CreditCard,
    Filter,
    Receipt,
    RefreshCcw,
    Search,
    Smartphone,
    TrendingDown,
    TrendingUp,
    Tv,
    Wallet,
    Wifi,
    X,
    XCircle,
    Zap,
    GraduationCap
} from 'lucide-react';

// project imports
import MainCard from 'ui-component/cards/MainCard';

const TRANSACTION_TYPE_OPTIONS = [
    { value: '', label: 'All', icon: <Filter size={16} /> },
    { value: 'WALLET_FUNDING', label: 'Wallet Funding', icon: <Wallet size={16} /> },
    { value: 'ADMIN_WALLET_FUNDING', label: 'Admin Wallet Funding', icon: <TrendingUp size={16} /> },
    { value: 'WALLET_DEDUCTING', label: 'Admin Wallet Deduct', icon: <TrendingDown size={16} /> },
    { value: 'DATA_ORDER', label: 'Data Order', icon: <Smartphone size={16} /> },
    { value: 'API_DATA_ORDER', label: 'API Data Order', icon: <Smartphone size={16} /> },
    { value: 'AIRTIME_ORDER', label: 'Airtime Order', icon: <Wifi size={16} /> },
    { value: 'API_AIRTIME_ORDER', label: 'API Airtime Order', icon: <Wifi size={16} /> },
    { value: 'ELECTRICITY_ORDER', label: 'Electricity Payment', icon: <Zap size={16} /> },
    { value: 'API_ELECTRICITY_ORDER', label: 'API Electricity Payment', icon: <Zap size={16} /> },
    { value: 'CABLE_ORDER', label: 'TV Payment', icon: <Tv size={16} /> },
    { value: 'API_CABLE_ORDER', label: 'API TV Payment', icon: <Tv size={16} /> },
    { value: 'RESULT_CHECK_ORDER', label: 'Result Check Payment', icon: <GraduationCap size={16} /> },
    { value: 'API_RESULT_CHECK_ORDER', label: 'API Result Check Payment', icon: <GraduationCap size={16} /> }
];

const formatAmount = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(Number(amount || 0));
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('en-NG').format(Number(value || 0));
};

const formatDate = (date) => {
    if (!date) return '--';
    try {
        return new Date(date).toLocaleString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return date;
    }
};

const prettifyText = (value) => {
    if (!value) return '--';
    return String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusColor = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'success') return 'success';
    if (normalized === 'pending') return 'warning';
    if (normalized === 'failed' || normalized === 'error') return 'error';
    return 'default';
};

const getStatusConfig = (status) => {
    const normalized = String(status || '').toLowerCase();

    if (normalized === 'success') {
        return {
            color: 'success',
            icon: <CheckCircle2 size={16} />,
            label: 'Successful'
        };
    }

    if (normalized === 'pending') {
        return {
            color: 'warning',
            icon: <Clock3 size={16} />,
            label: 'Pending'
        };
    }

    return {
        color: 'error',
        icon: <XCircle size={16} />,
        label: 'Failed'
    };
};

const getTransactionIcon = (transaction = {}) => {
    const type = String(transaction?.transactionType || '').toUpperCase();

    if (type === 'DATA_ORDER' || type === 'API_DATA_ORDER') return <Smartphone size={18} />;
    if (type === 'AIRTIME_ORDER' || type === 'API_AIRTIME_ORDER') return <Wifi size={18} />;
    if (type === 'ELECTRICITY_ORDER' || type === 'API_ELECTRICITY_ORDER') return <Zap size={18} />;
    if (type === 'CABLE_ORDER' || type === 'API_CABLE_ORDER') return <Tv size={18} />;
    if (type === 'RESULT_CHECK_ORDER' || type === 'API_RESULT_CHECK_ORDER') return <GraduationCap size={18} />;
    if (type === 'WALLET_DEDUCTING') return <TrendingDown size={18} />;
    if (type === 'WALLET_FUNDING' || type === 'ADMIN_WALLET_FUNDING') return <TrendingUp size={18} />;

    if (type.includes('DEDUCT') || type.includes('DEBIT') || type.includes('ORDER')) {
        return <TrendingDown size={18} />;
    }

    if (type.includes('FUND') || type.includes('CREDIT')) {
        return <TrendingUp size={18} />;
    }

    return <Wallet size={18} />;
};

const getTransactionTypeLabel = (type) => {
    const found = TRANSACTION_TYPE_OPTIONS.find((item) => item.value === type);
    return found?.label || prettifyText(type);
};

const ReceiptRow = ({ label, value, bold = false }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1.1 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>

        <Typography
            variant="body2"
            sx={{
                fontWeight: bold ? 700 : 500,
                textAlign: 'right',
                maxWidth: '65%',
                wordBreak: 'break-word'
            }}
        >
            {value || '--'}
        </Typography>
    </Stack>
);

export const TransactionReceiptModal = ({ open, onClose, selected }) => {
    const theme = useTheme();

    if (!selected) return null;

    const status = getStatusConfig(selected?.status);

    const copyRef = async () => {
        try {
            await navigator.clipboard.writeText(selected?.ref || '');
        } catch (error) {
            console.log('Copy failed', error);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    width: '100%',
                    height: { xs: '100vh', sm: '90vh' },
                    width: { xs: '100vw', sm: '560px' },
                    mx: 'auto',
                    overflow: 'auto',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                    // px: 2
                }}
            >
                <Box
                    sx={{
                        borderRadius: '28px',
                        overflow: 'hidden',
                        width: '100%',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.12)'
                    }}
                >
                    <Box
                        sx={{
                            px: 3,
                            pt: 3,
                            pb: 2.5,
                            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                                theme.palette.primary.main,
                                0.03
                            )} 100%)`
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                        color: 'primary.main'
                                    }}
                                >
                                    {getTransactionIcon(selected)}
                                </Avatar>

                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                        Transaction Receipt
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {getTransactionTypeLabel(selected?.transactionType)}
                                    </Typography>
                                </Box>
                            </Stack>

                            <IconButton onClick={onClose}>
                                <X size={18} />
                            </IconButton>
                        </Stack>

                        <Stack alignItems="center" spacing={1.4} sx={{ mt: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                Amount
                            </Typography>

                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 900,
                                    lineHeight: 1.1,
                                    textAlign: 'center'
                                }}
                            >
                                {formatAmount(selected?.amount, selected?.currency || 'NGN')}
                            </Typography>

                            <Chip
                                icon={status.icon}
                                label={status.label}
                                color={status.color}
                                sx={{
                                    borderRadius: '999px',
                                    px: 0.5,
                                    fontWeight: 700
                                }}
                            />

                            <Typography variant="body2" color="text.secondary">
                                {formatDate(selected?.createdAt)}
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ px: 3, py: 2.5 }}>
                        <Stack spacing={0}>
                            <ReceiptRow
                                label="Transaction Name"
                                value={selected?.name || getTransactionTypeLabel(selected?.transactionType)}
                                bold
                            />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <ReceiptRow
                                label="Description"
                                value={selected?.description || getTransactionTypeLabel(selected?.transactionType)}
                            />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <ReceiptRow label="Transaction Type" value={getTransactionTypeLabel(selected?.transactionType)} />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            {/* <ReceiptRow label="Service" value={selected?.service || '--'} />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <ReceiptRow label="Category" value={selected?.category || '--'} />
                            <Divider sx={{ borderStyle: 'dashed' }} /> */}

                            <ReceiptRow label="Payment Method" value={selected?.payment_method || '--'} />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <ReceiptRow label="Transaction ID" value={selected?.txId || selected?.id || '--'} />
                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ py: 1.1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Reference
                                </Typography>

                                <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: '70%' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            textAlign: 'right',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {selected?.ref || '--'}
                                    </Typography>

                                    <Tooltip title="Copy reference">
                                        <IconButton size="small" onClick={copyRef}>
                                            <Copy size={15} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>

                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <ReceiptRow label="Fee" value={formatAmount(selected?.fee, selected?.currency || 'NGN')} />
                            <Divider sx={{ borderStyle: 'dashed' }} />
                        </Stack>

                        {(selected?.details || selected?.processor) && (
                            <>
                                <Box sx={{ mt: 3, mb: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                        Extra Details
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: '18px',
                                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.primary.main, 0.08)
                                    }}
                                >
                                    <Stack spacing={0}>
                                        {selected?.details?.mobile_number ? (
                                            <>
                                                <ReceiptRow label="Mobile Number" value={selected.details.mobile_number} />
                                                <Divider sx={{ borderStyle: 'dashed' }} />
                                            </>
                                        ) : null}

                                        {selected?.details?.meter_number ? (
                                            <>
                                                <ReceiptRow label="Meter Number" value={selected.details.meter_number} />
                                                <Divider sx={{ borderStyle: 'dashed' }} />
                                            </>
                                        ) : null}

                                        {selected?.details?.smartcard_number ? (
                                            <>
                                                <ReceiptRow label="Smartcard Number" value={selected.details.smartcard_number} />
                                                <Divider sx={{ borderStyle: 'dashed' }} />
                                            </>
                                        ) : null}

                                        {selected?.details?.customer_name ? (
                                            <>
                                                <ReceiptRow label="Customer Name" value={selected.details.customer_name} />
                                                <Divider sx={{ borderStyle: 'dashed' }} />
                                            </>
                                        ) : null}

                                        {selected?.details?.balance ? (
                                            <>
                                                <ReceiptRow
                                                    label="Balance Before"
                                                    value={formatAmount(selected.details.balance.before, selected?.currency || 'NGN')}
                                                />
                                                <Divider sx={{ borderStyle: 'dashed' }} />
                                                <ReceiptRow
                                                    label="Balance After"
                                                    value={formatAmount(selected.details.balance.after, selected?.currency || 'NGN')}
                                                    bold
                                                />
                                            </>
                                        ) : null}
                                    </Stack>
                                </Box>
                            </>
                        )}

                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Button fullWidth variant="outlined" startIcon={<Copy size={16} />} onClick={copyRef}>
                                Copy Ref
                            </Button>

                            <Button fullWidth variant="contained" startIcon={<Receipt size={16} />} onClick={onClose}>
                                Done
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};
